/**
 * Stop Card
 *
 * Compact list-row card used by both the Atlas and Database stops
 * panels. Shows name, label, coords, link status, and exposes per-row
 * actions (select, edit, delete) via children-passed handlers.
 */

import React from 'react';
import { Box, Card, Checkbox, Chip, IconButton, Tooltip, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
export interface StopCardProps {
  id: string;
  name: string;
  label?: string;
  coordinates?: { lat: number; lng: number };
  selected: boolean;
  onToggleSelect: (id: string) => void;
  /** "new" = Atlas-only, "linked" = has uatStopId, "remote" = exists in DB only */
  linkState: 'new' | 'linked' | 'remote';
  uatStopId?: string;
  address?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
}

const linkBadgeMeta: Record<StopCardProps['linkState'], { color: 'default' | 'success' | 'info'; icon: React.ReactElement }> = {
  new: { color: 'default', icon: <Icon size={14} d={ICON_FALLBACKS.AddLocationAlt} /> },
  linked: { color: 'success', icon: <Icon size={14} name="link" /> },
  remote: { color: 'info', icon: <Icon size={14} name="link" /> },
};

const linkBadgeKey: Record<StopCardProps['linkState'], string> = {
  new: 'stops.card.badgeNew',
  linked: 'stops.card.badgeLinked',
  remote: 'stops.card.badgeRemote',
};

export const StopCard: React.FC<StopCardProps> = ({
  id,
  name,
  label,
  coordinates,
  selected,
  onToggleSelect,
  linkState,
  uatStopId,
  address,
  onEdit,
  onDelete,
  disabled,
}) => {
  const { t } = useTranslation('mapTransfer');
  const badge = linkBadgeMeta[linkState];
  const badgeLabel = t(linkBadgeKey[linkState]);

  return (
    <Card
      variant="outlined"
      sx={{
        p: 1,
        mb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Checkbox
        size="small"
        checked={selected}
        disabled={disabled}
        onChange={() => onToggleSelect(id)}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={600} noWrap title={name}>
            {name || label || id}
          </Typography>
          <Chip
            size="small"
            color={badge.color}
            icon={badge.icon}
            label={badgeLabel}
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap>
          {coordinates
            ? `${coordinates.lat.toFixed(5)}, ${coordinates.lng.toFixed(5)}`
            : t('stops.card.noCoordinates')}
          {address ? ` · ${address}` : ''}
          {uatStopId ? t('stops.card.uatPrefix', { value: uatStopId.substring(0, 8) }) : ''}
        </Typography>
      </Box>
      {onEdit && (
        <Tooltip title={t('stops.card.editTooltip')}>
          <span>
            <IconButton size="small" onClick={onEdit} disabled={disabled}>
              <Icon d={ICON_FALLBACKS.EditLocation} size={20} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title={t('stops.card.deleteTooltip')}>
          <span>
            <IconButton size="small" color="error" onClick={onDelete} disabled={disabled}>
              <Icon d={ICON_FALLBACKS.WrongLocation} size={20} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Card>
  );
};

export default StopCard;
